import { Sites } from '@ps/platform-message-framework/common/Sites';
import { isNull } from 'lodash';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Invoke } from '@swf/components/StarsWizard/Flow';
import { Country } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import { select } from '@swf/core/store';
import type { FlowConfig } from '@swf/core/types/Wizard';

import useRmfBRFlow from './useRmfBRFlow';
import useRmfCAONFlow from './useRmfCAONFlow';
import useRmfDEFlow from './useRmfDEFlow';
import useRmfDKFlow from './useRmfDKFlow';
import useRmfDefaultFlow from './useRmfDefaultFlow';
import useRmfESFlow from './useRmfESFlow';
import useRmfFRFlow from './useRmfFRFlow';
import useRmfGRFlow from './useRmfGRFlow';
import useRmfITFlow from './useRmfITFlow';
import useRmfPTFlow from './useRmfPTFlow';
import useRmfROFlow from './useRmfROFlow';
import useRmfServices, { Service } from './useRmfServices';
import useRmfUKFlow from './useRmfUKFlow';
import useRmfUSMIFlow from './useRmfUSMIFlow';

const useRmfFlow = (): FlowConfig => {
  const CAONFlow = useRmfCAONFlow();
  const DEFlow = useRmfDEFlow();
  const DKFlow = useRmfDKFlow();
  const UKFlow = useRmfUKFlow();
  const FRFlow = useRmfFRFlow();
  const ITFlow = useRmfITFlow();
  const PTFlow = useRmfPTFlow();
  const ROFlow = useRmfROFlow();
  const BRFlow = useRmfBRFlow();
  const ESFlow = useRmfESFlow();
  const GRFlow = useRmfGRFlow();
  const USMIFlow = useRmfUSMIFlow();
  const defaultFlow = useRmfDefaultFlow();
  const siteId = useSelector(select.cms.getSiteId);
  const country = useSelector(select.country.getCountry);
  const {
    data: { rmfPassed }
  } = useWizardContext();
  const exitWizard = useWizardExit();
  const isEmbedded = useSelector(select.device.isEmbedded);
  const { rmfRealMoneyInformationCheck } = useRmfServices();
  const isStandaloneRmf = useSelector(select.location.isStandAloneRMF);
  const checkRealMoney = useCallback(async () => {
    const { realMoneyInformation } = await rmfRealMoneyInformationCheck();

    if (isNull(realMoneyInformation)) {
      exitWizard();
    }
  }, [exitWizard, rmfRealMoneyInformationCheck]);

  if (isStandaloneRmf && !isEmbedded) {
    checkRealMoney();
  }

  const flow: Partial<Record<Sites | Country, FlowConfig>> = {
    [Sites.CA_ON]: CAONFlow,
    [Sites.DE]: DEFlow,
    [Sites.DEN]: DKFlow,
    [Sites.FRA]: FRFlow,
    [Sites.GR]: GRFlow,
    [Sites.ITA]: ITFlow,
    [Sites.POR]: PTFlow,
    [Sites.ROM]: ROFlow,
    [Sites.UK]: UKFlow,
    [Sites.SPA]: ESFlow,
    [Country.Brazil]: BRFlow,
    [Sites.USMI]: USMIFlow,
    [Sites.US_NJ]: USMIFlow,
    [Sites.US_PA]: USMIFlow
  };

  const result = flow[siteId] || flow[country as Country] || defaultFlow;

  if (!rmfPassed && isStandaloneRmf) {
    let flow = [new Invoke(Service.RmfUpdateFormData, result[0]), ...result];
    if (siteId === Sites.CA_ON) {
      const invokeUpdateCanadaFormFields = new Invoke(Service.RmfUpdateCanadaFormFields, result[0]);
      flow = [
        new Invoke(Service.RmfUpdateFormData, invokeUpdateCanadaFormFields),
        invokeUpdateCanadaFormFields,
        ...result
      ];
    }
    return flow;
  }

  return result;
};

export default useRmfFlow;
